var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from 'clsx';
import { parse } from 'query-string';
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Driver } from '@/components/functional/driver/Driver';
import { RfpCliInterviewTooltipOne, RfpCliInterviewTooltipTwo, RfpCliInterviewTooltipThree, } from '@/components/modules/pages/rfp/client/Tooltip/RfpCliInterviewTooltip';
import { useDriverSettings } from '@/hooks/ui/functional/useDriverSettings';
import { DRIVER_PORTAL_ROOT_CLASS } from '@/utils/constants/ui/driver';
import { apiCreateClientOfferViewEvent } from '../../../../actions/ClientRfpActions';
import { RFP_EVENT_TYPE_CLIENT_RFP_VIEW } from '../../../../define/consts/rfp/rfp_event';
import { propArrayShape, propShape, propLocation, } from '../../../../helpers/custom-prop-types';
import { compose } from '../../../../helpers/recompose';
import { scrollToWithHeaderHeight } from '../../../../helpers/ScrollUtils';
import { useDidMount, useUnmountRef } from '../../../../helpers/use/useHook';
import UserPreviewState from '../../../../helpers/user/UserPreviewState';
import Layout from '../../../Layout';
import Breadcrumb from '../../../Layout/Breadcrumb';
import AuthenticationHoc from '../../../System/AuthenticationHoc';
import RfpPageTitle from '../../../Ui/Rfp/RfpPageTitle';
import ClientRfpHoc from '../Hoc/ClientRfpHoc';
import RfpOneArrowLink from './Parts/Head/RfpOneArrowLink';
import InterviewContent from './Parts/Interview/InterviewContent';
import SendOffModal from './Parts/Interview/Modal/SendOffModal';
import RfpContentSubMenu from './Parts/SubMenu/RfpContentSubMenu';
import RfpOneSkeleton from './RfpOneSkeleton';
import { rfpInterviewContentUseCase } from './UseCase/RfpInterviewContentUseCase';
import { rfpOneUseCase } from './UseCase/RfpOneUseCase';
import { rfpSendOffModalUseCase } from './UseCase/RfpSendOffModalUseCase';
const RfpOneInterviewView = (props) => {
    const { location: { search }, offers: rawOffers, rfp, } = props;
    const { id, is_interview_view: isInterviewView, name } = rfp;
    const unmount = useUnmountRef();
    const isInterviewRef = useRef();
    isInterviewRef.current = isInterviewView;
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const profile = useSelector((state) => state.profile);
    /** プレビュー表示か（管理画面確認用） */
    const { isPreview } = UserPreviewState;
    const pans = useMemo(() => [
        {
            link: isPreview ? undefined : '/mypage/project',
            text: 'マイページ',
        },
        {
            text: name,
        },
    ], [name, isPreview]);
    const { isActionDisabled, isCanUpdateMember, isMember, isMemberAdmin, members, } = rfpOneUseCase.useMemberInfo(rfp, profile, auth);
    const isPreviewUser = UserPreviewState.isPreview;
    const { hideSendOffModal, isShowSendOffModal, modalValue, onSubmitSendOff: rawOnSubmitSendOff, showSendOffModal, } = rfpSendOffModalUseCase.useSendOffModal(id, dispatch);
    const { changeSelects, hasFile, isView, offers, onChangeIsView, onChangeRowApproach, onChangeRowSelect, onChangeSort, selects, sort, sortItems, viewItems, } = rfpInterviewContentUseCase.useContent(id, rawOffers, dispatch, unmount);
    const onClickRowSendOff = useCallback((value) => __awaiter(void 0, void 0, void 0, function* () {
        showSendOffModal([value]);
    }), [showSendOffModal]);
    const onSubmitSendOff = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        yield rawOnSubmitSendOff(values);
        changeSelects([]);
    }), [rawOnSubmitSendOff, changeSelects]);
    /** Driver 表示対象の Ref */
    const driverTargetOneRef = useRef(null);
    const driverTargetTwoRef = useRef(null);
    const driverTargetThreeRef = useRef(null);
    const { changeDriverState, changeDriverTarget, driverRef, getIsActiveDriverTarget, onClickDriverTarget, resetDriver, showDriver, } = useDriverSettings();
    /** Driver 設定 */
    const driverSettings = useMemo(() => [
        {
            onClickOverlay: changeDriverTarget,
            ref: driverTargetOneRef,
            scrollOffset: 235,
            tooltip: (_jsx(RfpCliInterviewTooltipOne, { onClickButton: changeDriverTarget })),
        },
        {
            onClickOverlay: changeDriverTarget,
            ref: driverTargetTwoRef,
            scrollOffset: 240,
            tooltip: (_jsx(RfpCliInterviewTooltipTwo, { onClickButton: changeDriverTarget })),
        },
        {
            onClickOverlay: changeDriverTarget,
            ref: driverTargetThreeRef,
            scrollOffset: 240,
            tooltip: (_jsx(RfpCliInterviewTooltipThree, { onClickButton: changeDriverTarget })),
        },
    ], [
        driverTargetOneRef,
        driverTargetThreeRef,
        driverTargetTwoRef,
        changeDriverTarget,
    ]);
    useEffect(() => {
        let active = true;
        if (!isPreviewUser && !isInterviewRef.current) {
            dispatch(apiCreateClientOfferViewEvent(id, RFP_EVENT_TYPE_CLIENT_RFP_VIEW));
            if (active) {
                showDriver();
            }
        }
        return () => {
            active = false;
            resetDriver();
        };
    }, [id, isInterviewRef, dispatch, isPreviewUser, resetDriver, showDriver]);
    useDidMount(() => {
        const { view_request_id: viewRequestId } = parse(search);
        if (!viewRequestId) {
            return;
        }
        scrollToWithHeaderHeight(`offer_row_${viewRequestId}`);
    });
    return (_jsxs(Layout, Object.assign({ mainClassName: clsx('l-main rfp_common_container rfp_client_one_container is_interview_view', DRIVER_PORTAL_ROOT_CLASS) }, { children: [_jsx(Helmet, { children: _jsx("title", { children: `${name} 面談先を決める｜PROFFIT` }) }), _jsx(Driver, { changeDriverState: changeDriverState, driverRef: driverRef, isLastInitialize: true, settings: driverSettings }), _jsx(Breadcrumb, { lists: pans }), _jsx(RfpPageTitle, { text: name }), _jsx("div", Object.assign({ className: 'rfp_container' }, { children: _jsxs("div", Object.assign({ className: 'rfp_content' }, { children: [_jsxs("div", Object.assign({ className: 'rfp_content_main' }, { children: [_jsx(RfpOneArrowLink, { activeIndex: 2, isPreview: isPreview, offers: rawOffers, rfp: rfp }), _jsx(InterviewContent, { changeSelects: changeSelects, driverTargetOneRef: driverTargetOneRef, driverTargetThreeRef: driverTargetThreeRef, driverTargetTwoRef: driverTargetTwoRef, getIsActiveDriverTarget: getIsActiveDriverTarget, hasFile: hasFile, isActionDisabled: isPreviewUser || isActionDisabled, isCharmPointDisabled: !isMember, isView: isView, offers: offers, onChangeIsView: onChangeIsView, onChangeRowApproach: onChangeRowApproach, onChangeRowSelect: onChangeRowSelect, onChangeSort: onChangeSort, onClickDriverTarget: onClickDriverTarget, onClickRowSendOff: onClickRowSendOff, rfp: rfp, selects: selects, showSendOffModal: showSendOffModal, sort: sort, sortItems: sortItems, viewItems: viewItems })] })), _jsx(RfpContentSubMenu, { isCanUpdateMember: isCanUpdateMember, isMemberAdmin: isMemberAdmin, members: members, rfp: rfp })] })) })), _jsx(SendOffModal, { hideModal: hideSendOffModal, isShow: isShowSendOffModal, modalValue: modalValue, onSubmit: onSubmitSendOff })] })));
};
RfpOneInterviewView.propTypes = {
    location: propLocation(),
    offers: propArrayShape().isRequired,
    rfp: propShape().isRequired,
};
const enhance = compose(AuthenticationHoc({
    isCanUseProffit: true,
    isPreClient: false,
    isPreview: true,
}), ClientRfpHoc({
    redirectCallback: ({ offers, rfp }) => {
        const isEnable = offers.length > 0;
        return !isEnable && `/project/client/${rfp.id}/offer`;
    },
}, RfpOneSkeleton));
export default enhance(RfpOneInterviewView);
